/**
 * AI Host Settings - Full Page Layout
 *
 * Redesigned settings page with grouped sidebar navigation.
 * Replaces the cramped modal with a spacious full-page layout.
 *
 * Groups:
 * - AI Configuration: Voice & Persona, Greetings, Operating Hours
 * - Profile: Branding, Contact & Support
 * - Order Management: Payments, Notifications
 * - Account: Team & Access, Billing & Usage (owner only)
 *
 * Author: Dine AI
 * Created: February 26, 2026
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Loader2,
  Clock,
  MessageSquare,
  Phone,
  Lock,
  Play,
  Pause,
  Volume2,
  Check,
  ImagePlus,
  Trash2,
  Image,
  Users,
  Link,
  CheckCircle,
  XCircle,
  Bell,
  Receipt,
  CreditCard,
  Wallet,
  Languages,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Eye,
  Search,
  Sparkles,
  Wand2,
  AlertTriangle,
  Monitor
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import VHStaffTeamManagement from '@/components/virtual-host/staff/VHStaffTeamManagement';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';
import { SpeechOverridesPanel } from '@/components/virtual-host/settings/speech-intelligence';

/**
 * Format phone number as (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

// Shift type for split hours
interface Shift {
  open: string;
  close: string;
  label?: string;
  last_order?: string;  // Last order cutoff time (e.g., "14:15")
}

type DayHours = { open: string; close: string; last_order?: string } | Shift[];

interface CuisineTemplate {
  cuisine_type: string;
  display_name: string;
}

interface CuisineTemplateDetail extends CuisineTemplate {
  hearing_corrections: Record<string, string>;
  alias_map: Record<string, string>;
  speaking_guide: Record<string, string>;
  speech_rules: string[];
}

interface SpeechOverrides {
  hearing_corrections?: Record<string, string>;
  alias_map?: Record<string, string>;
  speaking_guide?: Record<string, string>;
  speech_rules?: string[];
}

interface Settings {
  compliance_recording_notice?: string;
  recording_notice_enabled?: boolean;
  welcome_greeting?: string;
  pronunciation_guide?: string;
  cuisine_type?: string;
  speech_overrides?: SpeechOverrides;
  holiday_message?: string;
  holiday_greeting_enabled?: boolean;
  holiday_greeting_start_date?: string;
  holiday_greeting_end_date?: string;
  operating_hours?: { [day: string]: DayHours };
  working_days?: string[];
  wait_time_minutes?: number;
  next_shift_prep_minutes?: number;
  timezone?: string;
  human_contact_name?: string;
  human_contact_phone?: string;
  human_contact_email?: string;
  enable_reservations?: boolean;
  enable_catering?: boolean;
  enable_events?: boolean;
  order_notifications_enabled?: boolean;
  order_notify_phones?: string[];
  enable_large_order_alerts?: boolean;
  large_order_qty_threshold?: number;
  large_order_value_threshold?: number;
  transfer_on_allergen_mention?: boolean;
  require_payment_above?: number | null;  // NULL = customer chooses, value = require payment above
  voice_profile_id?: number;
  voice_profile_name?: string;
  vanity_code?: string;
  restaurant_slug?: string;
  payment_provider?: string;
  stripe_account_connected?: boolean;
  pos_provider?: string;
  pos_sync_enabled?: boolean;
  kitchen_print_enabled?: boolean;
  pos_connection_status?: string;
  pos_last_synced?: string;
}

interface VoiceProfile {
  profile_id: number;
  profile_name: string;
  profile_code: string;
  description?: string;
  sample_audio_url?: string;
}

interface BillingSummary {
  has_subscription: boolean;
  billing_period_start?: string;
  billing_period_end?: string;
  plan_name?: string;
  pickup_orders?: { count: number; fees: number; gmv: number; description: string };
  stripe_orders?: { count: number; fees: number; gmv: number; description: string };
  total_orders?: number;
  total_fees?: number;
  total_gmv?: number;
  message?: string;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const isSplitShift = (hours: DayHours | undefined): hours is Shift[] => {
  return Array.isArray(hours);
};

const getDefaultSingleShift = (): { open: string; close: string } => ({
  open: '09:00',
  close: '21:00'
});

const getDefaultSplitShifts = (): Shift[] => ([
  { open: '11:30', close: '14:30', label: 'Lunch', last_order: '14:15' },
  { open: '17:30', close: '22:30', label: 'Dinner', last_order: '22:15' }
]);

const toSingleShift = (shifts: Shift[]): { open: string; close: string; last_order?: string } => {
  if (shifts.length === 0) return getDefaultSingleShift();
  const lastShift = shifts[shifts.length - 1];
  return { open: shifts[0].open, close: lastShift.close, last_order: lastShift.last_order };
};

const getHolidayStatus = (enabled?: boolean, startDate?: string, endDate?: string, message?: string) => {
  if (!enabled) {
    return { status: 'disabled', label: '● DISABLED', color: 'text-gray-500' };
  }
  if (!message || !startDate || !endDate) {
    return { status: 'inactive', label: 'Configure dates below', color: 'text-gray-500' };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (today >= start && today <= end) {
    return { status: 'active', label: '● ACTIVE', color: 'text-green-600' };
  } else if (today < start) {
    const daysUntil = Math.ceil((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return { status: 'scheduled', label: `● SCHEDULED (starts in ${daysUntil} day${daysUntil !== 1 ? 's' : ''})`, color: 'text-yellow-600' };
  }
  return { status: 'inactive', label: '● EXPIRED', color: 'text-gray-500' };
};

// Navigation groups structure
const settingsGroups = [
  {
    label: 'AI CONFIGURATION',
    sections: [
      { id: 'voice', label: 'Voice & Persona', icon: Volume2 },
      { id: 'greetings', label: 'Greetings', icon: MessageSquare },
      { id: 'speech', label: 'Speech Intelligence', icon: Languages },
      { id: 'hours', label: 'Operating Hours', icon: Clock },
    ]
  },
  {
    label: 'PROFILE',
    sections: [
      { id: 'branding', label: 'Branding', icon: Image },
      { id: 'contact', label: 'Contact & Support', icon: Phone },
    ]
  },
  {
    label: 'ORDER MANAGEMENT',
    sections: [
      { id: 'payments', label: 'Payments', icon: CreditCard },
      { id: 'pos', label: 'POS Integration', icon: Monitor },
      { id: 'notifications', label: 'Notifications', icon: Bell },
      { id: 'services', label: 'Special Services', icon: Phone },
    ]
  },
  {
    label: 'ACCOUNT',
    ownerOnly: true,
    sections: [
      { id: 'team', label: 'Team & Access', icon: Users },
      { id: 'billing', label: 'Billing & Usage', icon: Receipt },
    ]
  },
];

const AIHostSettingsFullPage: React.FC = () => {
  usePageTitle('vhSettings');
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is VH Staff (from localStorage, set during login)
  const isStaff = localStorage.getItem('user_type') === 'vh_staff';

  const [activeSection, setActiveSection] = useState('voice');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [settings, setSettings] = useState<Settings>({
    compliance_recording_notice: 'This call may be recorded for quality and training purposes.',
    recording_notice_enabled: true,
    welcome_greeting: 'Thank you for calling! How can I help you today?',
    pronunciation_guide: '',
    cuisine_type: '',
    speech_overrides: undefined,
    holiday_message: '',
    holiday_greeting_enabled: false,
    holiday_greeting_start_date: '',
    holiday_greeting_end_date: '',
    operating_hours: {
      monday: { open: '09:00', close: '21:00' },
      tuesday: { open: '09:00', close: '21:00' },
      wednesday: { open: '09:00', close: '21:00' },
      thursday: { open: '09:00', close: '21:00' },
      friday: { open: '09:00', close: '21:00' },
      saturday: { open: '09:00', close: '21:00' },
      sunday: { open: '09:00', close: '21:00' }
    },
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    wait_time_minutes: 15,
    next_shift_prep_minutes: 30,
    timezone: 'America/New_York',
    human_contact_name: '',
    human_contact_phone: '',
    human_contact_email: '',
    enable_reservations: false,
    enable_catering: false,
    enable_events: false,
    order_notifications_enabled: false,
    order_notify_phones: ['', '', ''],
    enable_large_order_alerts: false,
    large_order_qty_threshold: 10,
    large_order_value_threshold: 500,
    transfer_on_allergen_mention: true,
    voice_profile_id: undefined,
    voice_profile_name: undefined,
    payment_provider: 'stripe',
    stripe_account_connected: false,
    pos_provider: undefined,
    pos_sync_enabled: false,
    kitchen_print_enabled: true,
    pos_connection_status: undefined,
    pos_last_synced: undefined
  });

  // Voice profiles
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([]);
  const [playingVoiceId, setPlayingVoiceId] = useState<number | null>(null);

  // Cuisine speech intelligence
  const [cuisineTemplates, setCuisineTemplates] = useState<CuisineTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CuisineTemplateDetail | null>(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(true);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState<number | null>(null);

  // AI Speech Generator
  const [speechMenuItems, setSpeechMenuItems] = useState<Array<{ item_name: string; description: string; section: string; cuisine_type: string; price: number }>>([]);
  const [speechMenuSearch, setSpeechMenuSearch] = useState('');
  const [speechGenerating, setSpeechGenerating] = useState(false);
  const [speechSelectedItem, setSpeechSelectedItem] = useState<string>('');
  const [speechSuggestions, setSpeechSuggestions] = useState<{
    item_name: string;
    hearing_corrections: Record<string, string>;
    alias_map: Record<string, string>;
    speaking_guide: Record<string, string>;
  } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Auto-Dictionary (batch speech generation)
  const [autoDictEnabled, setAutoDictEnabled] = useState(false);
  const [autoDictData, setAutoDictData] = useState<{ hearing_corrections: Record<string, string>; alias_map: Record<string, string>; speaking_guide: Record<string, string> } | null>(null);
  const [autoDictGeneratedAt, setAutoDictGeneratedAt] = useState<string | null>(null);
  const [autoDictGenerating, setAutoDictGenerating] = useState(false);
  const [autoDictToggling, setAutoDictToggling] = useState(false);
  const SAMPLE_TEXT = "Thank you for calling! I'm your AI assistant. How can I help you with your order today?";

  // Branding/Logo
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Operating hours validation errors: { "monday:open": true, "tuesday:last_order:1": true }
  const [hoursErrors, setHoursErrors] = useState<Record<string, boolean>>({});
  // Inline field validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Vanity URL
  const [vanityCode, setVanityCode] = useState<string>('');
  const [vanityCodeStatus, setVanityCodeStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [vanityCodeMessage, setVanityCodeMessage] = useState<string>('');
  const [vanityCodeSuggestion, setVanityCodeSuggestion] = useState<string>('');
  const [restaurantSlug, setRestaurantSlug] = useState<string>('');
  const vanityCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Billing
  const [billingData, setBillingData] = useState<BillingSummary | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  // Stripe Connect
  const [connectingStripe, setConnectingStripe] = useState(false);

  // Handle URL hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validSections = ['voice', 'greetings', 'speech', 'hours', 'branding', 'contact', 'payments', 'pos', 'notifications', 'services', 'team', 'billing'];
    if (hash && validSections.includes(hash)) {
      setActiveSection(hash);
    } else if (!hash) {
      setActiveSection('voice');
      window.location.hash = 'voice';
    }
  }, [location.hash]);

  // Warn on browser navigation (close tab, back button) with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Load data on mount
  useEffect(() => {
    fetchSettings();
    fetchVoiceProfiles();
    fetchLogo();
    fetchCuisineTemplates();
  }, []);

  // Fetch billing when billing section is active
  useEffect(() => {
    if (activeSection === 'billing') {
      fetchBillingSummary();
    }
  }, [activeSection]);

  // Fetch menu items when speech section is active
  useEffect(() => {
    if (activeSection === 'speech' && speechMenuItems.length === 0) {
      fetchSpeechMenuItems();
    }
  }, [activeSection]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/virtual-host/settings');
      const data = response.data;

      setSettings({
        compliance_recording_notice: data.compliance_recording_notice || 'This call may be recorded for quality and training purposes.',
        recording_notice_enabled: data.recording_notice_enabled !== undefined ? data.recording_notice_enabled : true,
        welcome_greeting: data.welcome_greeting || settings.welcome_greeting,
        pronunciation_guide: data.pronunciation_guide || '',
        cuisine_type: data.cuisine_type || '',
        speech_overrides: data.speech_overrides || undefined,
        holiday_message: data.holiday_message || '',
        holiday_greeting_enabled: data.holiday_greeting_enabled || false,
        holiday_greeting_start_date: data.holiday_greeting_start_date || '',
        holiday_greeting_end_date: data.holiday_greeting_end_date || '',
        operating_hours: data.operating_hours || settings.operating_hours,
        working_days: data.working_days || settings.working_days,
        wait_time_minutes: data.wait_time_minutes || 15,
        next_shift_prep_minutes: data.next_shift_prep_minutes ?? 30,
        timezone: data.timezone || 'America/New_York',
        human_contact_name: data.human_contact_name || '',
        human_contact_phone: data.human_contact_phone || '',
        human_contact_email: data.human_contact_email || '',
        enable_reservations: data.enable_reservations || false,
        enable_catering: data.enable_catering || false,
        enable_events: data.enable_events || false,
        order_notifications_enabled: data.order_notifications_enabled || false,
        order_notify_phones: [
          data.order_notify_phones?.[0] || '',
          data.order_notify_phones?.[1] || '',
          data.order_notify_phones?.[2] || ''
        ],
        enable_large_order_alerts: data.enable_large_order_alerts ?? false,
        large_order_qty_threshold: data.large_order_qty_threshold ?? 10,
        large_order_value_threshold: data.large_order_value_threshold ?? 500,
        transfer_on_allergen_mention: data.transfer_on_allergen_mention ?? true,
        voice_profile_id: data.voice_profile_id || undefined,
        voice_profile_name: data.voice_profile_name || undefined,
        vanity_code: data.vanity_code || '',
        restaurant_slug: data.restaurant_slug || '',
        payment_provider: data.payment_provider || 'stripe',
        stripe_account_connected: data.stripe_account_connected ?? false,
        pos_provider: data.pos_provider || undefined,
        pos_sync_enabled: data.pos_sync_enabled ?? false,
        kitchen_print_enabled: data.kitchen_print_enabled ?? true,
        pos_connection_status: data.pos_connection_status || undefined,
        pos_last_synced: data.pos_last_synced || undefined
      });

      // Auto-dictionary state
      setAutoDictEnabled(data.auto_speech_enabled || false);
      setAutoDictData(data.auto_speech_overrides || null);
      setAutoDictGeneratedAt(data.auto_speech_generated_at || null);

      setVanityCode(data.vanity_code || '');
      setRestaurantSlug(data.restaurant_slug || '');

      if (!data.vanity_code) {
        fetchVanitySuggestion();
      } else {
        setVanityCodeStatus('available');
      }

      // Load cuisine template detail if cuisine_type is set
      if (data.cuisine_type) {
        fetchCuisineTemplateDetail(data.cuisine_type);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceProfiles = async () => {
    try {
      const response = await apiClient.get('/virtual-host/settings/voice-profiles');
      setVoiceProfiles(response.data);
    } catch (error) {
      console.error('Error fetching voice profiles:', error);
    }
  };

  const fetchCuisineTemplates = async () => {
    try {
      const response = await apiClient.get('/virtual-host/settings/cuisine-templates');
      setCuisineTemplates(response.data);
    } catch (error) {
      console.error('Error fetching cuisine templates:', error);
    }
  };

  const fetchCuisineTemplateDetail = async (cuisineType: string) => {
    try {
      const response = await apiClient.get(`/virtual-host/settings/cuisine-template/${cuisineType}`);
      const data = response.data;
      // Normalize: ensure dict fields are objects and speech_rules is always an array
      setSelectedTemplate({
        ...data,
        hearing_corrections: (data.hearing_corrections && typeof data.hearing_corrections === 'object' && !Array.isArray(data.hearing_corrections)) ? data.hearing_corrections : {},
        alias_map: (data.alias_map && typeof data.alias_map === 'object' && !Array.isArray(data.alias_map)) ? data.alias_map : {},
        speaking_guide: (data.speaking_guide && typeof data.speaking_guide === 'object' && !Array.isArray(data.speaking_guide)) ? data.speaking_guide : {},
        speech_rules: Array.isArray(data.speech_rules) ? data.speech_rules : [],
      });
    } catch (error) {
      console.error('Error fetching cuisine template detail:', error);
      setSelectedTemplate(null);
    }
  };

  // AI Speech Generator: fetch menu items
  const fetchSpeechMenuItems = async () => {
    try {
      const response = await apiClient.get('/virtual-host/settings/menu-items');
      setSpeechMenuItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching menu items for speech:', error);
    }
  };

  // AI Speech Generator: generate suggestions for a menu item
  const generateSpeechForItem = async (item: { item_name: string; description: string; section: string; cuisine_type: string }) => {
    setSpeechGenerating(true);
    setSpeechSelectedItem(item.item_name);
    setSpeechSuggestions(null);
    try {
      const response = await apiClient.post('/virtual-host/settings/generate-speech', {
        item_name: item.item_name,
        item_description: item.description,
        cuisine_type: item.cuisine_type || settings.cuisine_type || '',
        section: item.section
      });
      if (response.data.success) {
        setSpeechSuggestions({
          item_name: response.data.item_name,
          ...response.data.suggestions
        });
        toast.success(`Generated speech intelligence for "${item.item_name}"`);
      }
    } catch (error: any) {
      console.error('Error generating speech intelligence:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate speech intelligence');
    } finally {
      setSpeechGenerating(false);
    }
  };

  // AI Speech Generator: add a suggestion to restaurant overrides
  const addSuggestionToOverrides = (type: 'hearing_corrections' | 'alias_map' | 'speaking_guide', key: string, value: string) => {
    addKeyValuePair(type, key, value);
    // Remove from suggestions
    if (speechSuggestions) {
      const updated = { ...speechSuggestions };
      const section = { ...updated[type] };
      delete section[key];
      updated[type] = section;
      setSpeechSuggestions(updated);
    }
    toast.success(`Added to overrides: "${key}"`);
  };

  // Auto-Dictionary: batch generate
  const generateAutoDictionary = async () => {
    setAutoDictGenerating(true);
    try {
      const response = await apiClient.post('/virtual-host/settings/generate-speech-batch', {}, { timeout: 120000 });
      if (response.data.success) {
        setAutoDictData(response.data.auto_speech_overrides);
        setAutoDictEnabled(true);
        setAutoDictGeneratedAt(new Date().toISOString());
        const stats = response.data.stats;
        toast.success(
          `Auto-Dictionary generated: ${stats.hearing_corrections} corrections, ${stats.aliases} aliases, ${stats.pronunciations} pronunciations`
        );
        if (response.data.errors?.length) {
          toast.warning(`${response.data.errors.length} batch(es) had errors — partial results saved`);
        }
      }
    } catch (error: any) {
      console.error('Error generating auto-dictionary:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate auto-dictionary');
    } finally {
      setAutoDictGenerating(false);
    }
  };

  // Auto-Dictionary: toggle on/off
  const toggleAutoDictionary = async (enabled: boolean) => {
    setAutoDictToggling(true);
    try {
      const response = await apiClient.put('/virtual-host/settings/auto-speech-toggle', { enabled });
      if (response.data.success) {
        setAutoDictEnabled(enabled);
        toast.success(`Auto-Dictionary ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error: any) {
      console.error('Error toggling auto-dictionary:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle auto-dictionary');
    } finally {
      setAutoDictToggling(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await apiClient.get('/virtual-host/settings/branding/logo');
      if (response.data.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  const fetchBillingSummary = useCallback(async () => {
    setLoadingBilling(true);
    try {
      const response = await apiClient.get('/virtual-host/settings/billing-summary');
      setBillingData(response.data);
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      setBillingData(null);
    } finally {
      setLoadingBilling(false);
    }
  }, []);

  const fetchVanitySuggestion = async () => {
    try {
      const response = await apiClient.get('/virtual-host/settings/vanity-code/suggest');
      if (response.data.suggestion) {
        setVanityCodeSuggestion(response.data.suggestion);
      }
    } catch (error) {
      console.error('Error fetching vanity suggestion:', error);
    }
  };

  /**
   * Validate operating hours before save.
   * Returns true if valid, false if errors found (toast shown).
   */
  const validateOperatingHours = (): boolean => {
    const workingDays = settings.working_days || [];
    const opHours = settings.operating_hours || {};
    const errors: Record<string, boolean> = {};
    const messages: string[] = [];

    const timeToMinutes = (t: string): number | null => {
      if (!t) return null;
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h) || isNaN(m)) return null;
      return h * 60 + m;
    };

    const formatTime = (t: string): string => {
      const mins = timeToMinutes(t);
      if (mins === null) return t;
      const h = Math.floor(mins / 60) % 12 || 12;
      const m = mins % 60;
      const period = Math.floor(mins / 60) < 12 ? 'AM' : 'PM';
      return `${h}:${m.toString().padStart(2, '0')} ${period}`;
    };

    const dayLabel = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

    for (const dayKey of workingDays) {
      const hours = opHours[dayKey];
      if (!hours) continue;

      if (isSplitShift(hours)) {
        // Validate each shift
        for (let i = 0; i < hours.length; i++) {
          const shift = hours[i];
          const openMins = timeToMinutes(shift.open);
          const closeMins = timeToMinutes(shift.close);
          const loMins = timeToMinutes(shift.last_order || '');
          const label = shift.label || `Shift ${i + 1}`;

          if (openMins !== null && closeMins !== null && openMins >= closeMins) {
            errors[`${dayKey}:open:${i}`] = true;
            errors[`${dayKey}:close:${i}`] = true;
            messages.push(`${dayLabel(dayKey)} ${label}: Open time must be before close time`);
          }

          if (loMins !== null && closeMins !== null && loMins >= closeMins) {
            errors[`${dayKey}:last_order:${i}`] = true;
            messages.push(`${dayLabel(dayKey)} ${label}: Last Order (${formatTime(shift.last_order!)}) must be before close (${formatTime(shift.close)})`);
          }

          if (loMins !== null && openMins !== null && loMins <= openMins) {
            errors[`${dayKey}:last_order:${i}`] = true;
            messages.push(`${dayLabel(dayKey)} ${label}: Last Order must be after open time`);
          }
        }

        // Check overlap between shifts
        if (hours.length >= 2) {
          for (let i = 0; i < hours.length - 1; i++) {
            const closeMins = timeToMinutes(hours[i].close);
            const nextOpenMins = timeToMinutes(hours[i + 1].open);
            if (closeMins !== null && nextOpenMins !== null && closeMins > nextOpenMins) {
              errors[`${dayKey}:close:${i}`] = true;
              errors[`${dayKey}:open:${i + 1}`] = true;
              messages.push(`${dayLabel(dayKey)}: ${hours[i].label || 'Shift 1'} close overlaps with ${hours[i + 1].label || 'Shift 2'} open`);
            }
          }
        }
      } else {
        // Single shift
        const singleHours = hours as { open: string; close: string; last_order?: string };
        const openMins = timeToMinutes(singleHours.open);
        const closeMins = timeToMinutes(singleHours.close);
        const loMins = timeToMinutes(singleHours.last_order || '');

        if (openMins !== null && closeMins !== null && openMins >= closeMins) {
          errors[`${dayKey}:open`] = true;
          errors[`${dayKey}:close`] = true;
          messages.push(`${dayLabel(dayKey)}: Open time must be before close time`);
        }

        if (loMins !== null && closeMins !== null && loMins >= closeMins) {
          errors[`${dayKey}:last_order`] = true;
          messages.push(`${dayLabel(dayKey)}: Last Order (${formatTime(singleHours.last_order!)}) must be before close (${formatTime(singleHours.close)})`);
        }

        if (loMins !== null && openMins !== null && loMins <= openMins) {
          errors[`${dayKey}:last_order`] = true;
          messages.push(`${dayLabel(dayKey)}: Last Order must be after open time`);
        }
      }
    }

    setHoursErrors(errors);

    if (messages.length > 0) {
      toast.error(messages[0], { duration: 5000 });
      return false;
    }
    return true;
  };

  /** Validate a single field on blur and update error state */
  const validateFieldOnBlur = (dayKey: string, field: 'open' | 'close' | 'last_order', shiftIndex?: number) => {
    const hours = settings.operating_hours?.[dayKey];
    if (!hours) return;

    const timeToMinutes = (t: string): number | null => {
      if (!t) return null;
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h) || isNaN(m)) return null;
      return h * 60 + m;
    };

    const errorKey = shiftIndex !== undefined ? `${dayKey}:${field}:${shiftIndex}` : `${dayKey}:${field}`;

    setHoursErrors(prev => {
      const next = { ...prev };

      if (isSplitShift(hours) && shiftIndex !== undefined) {
        const shift = hours[shiftIndex];
        const openMins = timeToMinutes(shift.open);
        const closeMins = timeToMinutes(shift.close);
        const loMins = timeToMinutes(shift.last_order || '');

        if (field === 'open' || field === 'close') {
          const hasError = openMins !== null && closeMins !== null && openMins >= closeMins;
          next[`${dayKey}:open:${shiftIndex}`] = hasError;
          next[`${dayKey}:close:${shiftIndex}`] = hasError;
        }
        if (field === 'last_order') {
          next[errorKey] = (loMins !== null && closeMins !== null && loMins >= closeMins)
            || (loMins !== null && openMins !== null && loMins <= openMins);
        }
      } else if (!isSplitShift(hours)) {
        const single = hours as { open: string; close: string; last_order?: string };
        const openMins = timeToMinutes(single.open);
        const closeMins = timeToMinutes(single.close);
        const loMins = timeToMinutes(single.last_order || '');

        if (field === 'open' || field === 'close') {
          const hasError = openMins !== null && closeMins !== null && openMins >= closeMins;
          next[`${dayKey}:open`] = hasError;
          next[`${dayKey}:close`] = hasError;
        }
        if (field === 'last_order') {
          next[errorKey] = (loMins !== null && closeMins !== null && loMins >= closeMins)
            || (loMins !== null && openMins !== null && loMins <= openMins);
        }
      }

      return next;
    });
  };

  // Speech override helpers
  const updateSpeechOverride = (section: keyof SpeechOverrides, value: any) => {
    const current = settings.speech_overrides || {};
    const updated = { ...current, [section]: value };
    // Clean up empty sections
    const cleaned: SpeechOverrides = {};
    if (updated.hearing_corrections && Object.keys(updated.hearing_corrections).length > 0)
      cleaned.hearing_corrections = updated.hearing_corrections;
    if (updated.alias_map && Object.keys(updated.alias_map).length > 0)
      cleaned.alias_map = updated.alias_map;
    if (updated.speaking_guide && Object.keys(updated.speaking_guide).length > 0)
      cleaned.speaking_guide = updated.speaking_guide;
    if (updated.speech_rules && updated.speech_rules.length > 0)
      cleaned.speech_rules = updated.speech_rules;
    updateSetting('speech_overrides', Object.keys(cleaned).length > 0 ? cleaned : undefined);
  };

  const addKeyValuePair = (section: 'hearing_corrections' | 'alias_map' | 'speaking_guide', key: string, value: string) => {
    if (!key.trim() || !value.trim()) return;
    const current = settings.speech_overrides?.[section] || {};
    updateSpeechOverride(section, { ...current, [key.trim().toLowerCase()]: value.trim() });
  };

  const handleCuisineTypeChange = (cuisineType: string) => {
    updateSetting('cuisine_type', cuisineType || '');
    if (cuisineType) {
      fetchCuisineTemplateDetail(cuisineType);
    } else {
      setSelectedTemplate(null);
    }
  };

  const handleSave = async () => {
    // Validate operating hours before saving
    if (!validateOperatingHours()) return;

    // --- Field-level validation ---
    const errors: string[] = [];

    // Text length limits
    if (settings.welcome_greeting && settings.welcome_greeting.length > 500) {
      errors.push('Welcome greeting must be 500 characters or less');
    }
    if (settings.pronunciation_guide && settings.pronunciation_guide.length > 2000) {
      errors.push('Pronunciation guide must be 2,000 characters or less');
    }
    if (settings.holiday_message && settings.holiday_message.length > 500) {
      errors.push('Holiday message must be 500 characters or less');
    }
    if (settings.human_contact_name && settings.human_contact_name.length > 100) {
      errors.push('Contact name must be 100 characters or less');
    }

    // Email format
    if (settings.human_contact_email && settings.human_contact_email.trim()) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(settings.human_contact_email)) {
        errors.push('Please enter a valid email address');
      }
    }

    // Contact phone completeness
    if (settings.human_contact_phone && settings.human_contact_phone.trim()) {
      const phoneDigits = settings.human_contact_phone.replace(/\D/g, '');
      if (phoneDigits.length > 0 && phoneDigits.length < 10) {
        errors.push('Contact phone must be at least 10 digits');
      }
    }

    // Notification phone completeness
    if (settings.order_notifications_enabled && settings.order_notify_phones) {
      const filledPhones = settings.order_notify_phones.filter(p => p && p.trim());
      for (let i = 0; i < filledPhones.length; i++) {
        const digits = filledPhones[i].replace(/\D/g, '');
        if (digits.length > 0 && digits.length < 10) {
          errors.push(`Notification phone ${i + 1} must be at least 10 digits`);
        }
      }
    }

    // Numeric ranges
    if (settings.wait_time_minutes !== undefined && settings.wait_time_minutes !== null) {
      if (settings.wait_time_minutes < 5 || settings.wait_time_minutes > 120) {
        errors.push('Wait time must be between 5 and 120 minutes');
      }
    }
    if (settings.next_shift_prep_minutes !== undefined && settings.next_shift_prep_minutes !== null) {
      if (settings.next_shift_prep_minutes < 5 || settings.next_shift_prep_minutes > 120) {
        errors.push('Prep time must be between 5 and 120 minutes');
      }
    }

    // Holiday date range
    if (settings.holiday_greeting_enabled &&
        settings.holiday_greeting_start_date && settings.holiday_greeting_end_date) {
      if (settings.holiday_greeting_start_date > settings.holiday_greeting_end_date) {
        errors.push('Holiday start date must be before end date');
      }
    }

    // Payment threshold
    if (settings.require_payment_above !== null && settings.require_payment_above !== undefined) {
      if (settings.require_payment_above < 0 || settings.require_payment_above > 10000) {
        errors.push('Payment threshold must be between $0 and $10,000');
      }
    }

    // Large order threshold
    if (settings.large_order_value_threshold !== undefined && settings.large_order_value_threshold !== null) {
      if (settings.large_order_value_threshold < 0 || settings.large_order_value_threshold > 100000) {
        errors.push('Large order threshold must be between $0 and $100,000');
      }
    }

    // Welcome greeting must include "AI" for FCC compliance
    if (settings.welcome_greeting && !settings.welcome_greeting.toLowerCase().includes('ai')) {
      errors.push('Your greeting must include "AI" (e.g., "AI assistant", "AI host") to comply with FCC disclosure requirements.');
    }

    if (errors.length > 0) {
      toast.error(errors[0], { duration: 5000 });
      return;
    }

    try {
      setSaving(true);

      const dataToSend: any = {};
      Object.entries(settings).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          dataToSend[key] = value;
        }
      });

      // Filter operating_hours to only include working days
      if (dataToSend.operating_hours && dataToSend.working_days) {
        const filteredHours: { [day: string]: any } = {};
        dataToSend.working_days.forEach((day: string) => {
          if (dataToSend.operating_hours[day]) {
            filteredHours[day] = dataToSend.operating_hours[day];
          }
        });
        dataToSend.operating_hours = filteredHours;
      }

      // Explicitly include cuisine_type even when empty (to clear it)
      if ('cuisine_type' in settings) {
        dataToSend.cuisine_type = settings.cuisine_type || null;
      }

      // Filter empty phone numbers
      if (dataToSend.order_notify_phones) {
        const filteredPhones = dataToSend.order_notify_phones.filter(
          (phone: string) => phone && phone.trim() !== ''
        );
        if (filteredPhones.length > 0) {
          dataToSend.order_notify_phones = filteredPhones;
        } else {
          delete dataToSend.order_notify_phones;
        }
      }

      await apiClient.put('/virtual-host/settings', dataToSend);
      setIsDirty(false);
      toast.success('Settings saved successfully!', { duration: 4000 });
    } catch (error: any) {
      console.error('Error saving settings:', error);
      const detail = error.response?.data?.detail;
      let message = 'Failed to save settings';
      if (Array.isArray(detail)) {
        message = detail.map((e: any) => e.msg).join(', ');
      } else if (typeof detail === 'string') {
        message = detail;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof Settings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const updateHours = (day: string, field: 'open' | 'close' | 'last_order', value: string) => {
    setSettings(prev => {
      const currentHours = prev.operating_hours?.[day];
      if (isSplitShift(currentHours)) {
        const newShifts = [...currentHours];
        newShifts[0] = { ...newShifts[0], [field]: value };
        return {
          ...prev,
          operating_hours: { ...prev.operating_hours, [day]: newShifts }
        };
      }
      return {
        ...prev,
        operating_hours: {
          ...prev.operating_hours,
          [day]: { ...(currentHours as { open: string; close: string } || getDefaultSingleShift()), [field]: value }
        }
      };
    });
    setIsDirty(true);
  };

  const updateShiftHours = (day: string, shiftIndex: number, field: 'open' | 'close' | 'last_order', value: string) => {
    setSettings(prev => {
      const currentHours = prev.operating_hours?.[day];
      const shifts = isSplitShift(currentHours) ? [...currentHours] : getDefaultSplitShifts();
      shifts[shiftIndex] = { ...shifts[shiftIndex], [field]: value };
      return {
        ...prev,
        operating_hours: { ...prev.operating_hours, [day]: shifts }
      };
    });
    setIsDirty(true);
  };

  const toggleSplitShift = (day: string) => {
    setSettings(prev => {
      const currentHours = prev.operating_hours?.[day];
      if (isSplitShift(currentHours)) {
        return {
          ...prev,
          operating_hours: { ...prev.operating_hours, [day]: toSingleShift(currentHours) }
        };
      }
      return {
        ...prev,
        operating_hours: { ...prev.operating_hours, [day]: getDefaultSplitShifts() }
      };
    });
    setIsDirty(true);
  };

  const toggleWorkingDay = (day: string) => {
    const workingDays = settings.working_days || [];
    if (workingDays.includes(day)) {
      updateSetting('working_days', workingDays.filter(d => d !== day));
    } else {
      updateSetting('working_days', [...workingDays, day]);
    }
  };

  const updateNotifyPhone = (index: number, value: string) => {
    const formatted = formatPhoneNumber(value);
    setSettings(prev => {
      const phones = [...(prev.order_notify_phones || ['', '', ''])];
      phones[index] = formatted;
      return { ...prev, order_notify_phones: phones };
    });
    setIsDirty(true);
  };

  const copyHoursToAll = (sourceDay: string) => {
    const sourceHours = settings.operating_hours?.[sourceDay];
    if (!sourceHours) return;

    const allDaysHours: any = {};
    DAYS_OF_WEEK.forEach(day => {
      if (isSplitShift(sourceHours)) {
        allDaysHours[day.key] = sourceHours.map(shift => ({ ...shift }));
      } else {
        allDaysHours[day.key] = { ...sourceHours };
      }
    });

    updateSetting('operating_hours', allDaysHours);
    toast.success(`Copied ${sourceDay} hours to all days`);
  };

  // Voice preview
  const playVoiceSample = async (profile: VoiceProfile) => {
    if (playingVoiceId === profile.profile_id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoiceId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    try {
      setIsGeneratingAudio(profile.profile_id);
      const response = await apiClient.post('/virtual-host/settings/voice-preview', {
        voice_id: profile.profile_code,
        text: SAMPLE_TEXT
      }, { responseType: 'blob' });

      const audioBlob = new Blob([response.data], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingVoiceId(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setPlayingVoiceId(null);
        toast.error('Failed to play voice sample');
        URL.revokeObjectURL(audioUrl);
      };

      setPlayingVoiceId(profile.profile_id);
      setIsGeneratingAudio(null);
      await audio.play();
    } catch (error) {
      console.error('Error playing voice sample:', error);
      setIsGeneratingAudio(null);
      toast.error('Voice preview not available yet');
    }
  };

  // Logo handlers
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, or WebP image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/virtual-host/settings/branding/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setLogoUrl(response.data.logo_url);
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error(response.data.message || 'Failed to upload logo');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!logoUrl) return;
    try {
      setDeletingLogo(true);
      const response = await apiClient.delete('/virtual-host/settings/branding/remove-logo');
      if (response.data.success) {
        setLogoUrl(null);
        toast.success('Logo removed successfully!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove logo');
    } finally {
      setDeletingLogo(false);
    }
  };

  // Vanity code handlers
  const checkVanityCodeAvailability = useCallback(async (code: string) => {
    if (!code || code.length < 3) {
      setVanityCodeStatus('idle');
      setVanityCodeMessage('');
      return;
    }

    setVanityCodeStatus('checking');
    try {
      const response = await apiClient.get(`/virtual-host/settings/vanity-code/check/${encodeURIComponent(code)}`);
      if (response.data.available) {
        setVanityCodeStatus('available');
        setVanityCodeMessage(response.data.message || 'Available!');
      } else {
        setVanityCodeStatus('taken');
        setVanityCodeMessage(response.data.message || 'Already taken');
        if (response.data.suggestion) setVanityCodeSuggestion(response.data.suggestion);
      }
    } catch (error: any) {
      setVanityCodeStatus('invalid');
      setVanityCodeMessage(error.response?.data?.message || 'Invalid format');
    }
  }, []);

  const handleVanityCodeChange = (value: string) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setVanityCode(normalized);

    if (vanityCheckTimeoutRef.current) clearTimeout(vanityCheckTimeoutRef.current);
    vanityCheckTimeoutRef.current = setTimeout(() => {
      checkVanityCodeAvailability(normalized);
    }, 500);
  };

  const handleSaveVanityCode = async () => {
    if (vanityCodeStatus !== 'available' && vanityCode !== '') {
      toast.error('Please enter a valid, available vanity URL');
      return;
    }
    try {
      setSaving(true);
      await apiClient.put('/virtual-host/settings', { vanity_code: vanityCode || null });
      toast.success('Staff Portal URL saved!');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save vanity URL');
    } finally {
      setSaving(false);
    }
  };

  // Build navigation sections (filter out Account group for staff)
  const visibleGroups = settingsGroups.filter(group => !group.ownerOnly || !isStaff);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (isDirty) {
                    setShowUnsavedModal(true);
                    return;
                  }
                  navigate(isStaff ? '/vh-staff-dashboard' : '/restaurant-dashboard');
                }}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back to Dashboard</span>
              </button>
            </div>
            <h1 className="text-xl font-bold text-gray-900">AI Host Settings</h1>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#FDB913] to-purple-500 hover:from-[#e5a511] hover:to-purple-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 shadow-lg"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Sidebar Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sticky top-24">
              <nav className="space-y-6">
                {visibleGroups.map((group) => (
                  <div key={group.label}>
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 px-4 pt-2 border-t border-gray-100 first:border-0 first:pt-0">
                      {group.label}
                    </h3>
                    <div className="space-y-1">
                      {group.sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => {
                            setActiveSection(section.id);
                            window.location.hash = section.id;
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                            activeSection === section.id
                              ? 'bg-[#FDB913] text-black font-semibold'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          <section.icon className="h-5 w-5" />
                          <span className="text-sm font-medium">{section.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Right Content Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3"
          >
            {/* Voice & Persona Section */}
            {activeSection === 'voice' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Volume2 className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Voice & Persona</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">Select a voice for your AI assistant. Click play to preview.</p>

                {/* Current Selection Badge */}
                {settings.voice_profile_name && (
                  <div className="flex items-center gap-2 mb-4">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">
                      Current: {settings.voice_profile_name}
                    </span>
                  </div>
                )}

                {/* Voice Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {voiceProfiles.map((profile) => (
                    <div
                      key={profile.profile_id}
                      onClick={() => {
                        updateSetting('voice_profile_id', profile.profile_id);
                        updateSetting('voice_profile_name', profile.profile_name);
                      }}
                      className={`relative p-4 border rounded-xl cursor-pointer transition-all ${
                        settings.voice_profile_id === profile.profile_id
                          ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            playVoiceSample(profile);
                          }}
                          disabled={isGeneratingAudio === profile.profile_id}
                          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                            playingVoiceId === profile.profile_id
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {isGeneratingAudio === profile.profile_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : playingVoiceId === profile.profile_id ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4 ml-0.5" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{profile.profile_name}</p>
                          <p className="text-sm text-gray-500 truncate">{profile.description}</p>
                        </div>
                        {settings.voice_profile_id === profile.profile_id && (
                          <div className="absolute top-3 right-3">
                            <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Greetings Section */}
            {activeSection === 'greetings' && (
              <div className="space-y-6">
                {/* Recording Notice - Toggle + Editable */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Recording Notice
                    </label>
                    <button
                      type="button"
                      onClick={() => updateSetting('recording_notice_enabled', !settings.recording_notice_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.recording_notice_enabled ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.recording_notice_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  {settings.recording_notice_enabled && (
                    <>
                      <textarea
                        value={settings.compliance_recording_notice}
                        onChange={(e) => updateSetting('compliance_recording_notice', e.target.value)}
                        placeholder="This call may be recorded for quality and training purposes."
                        maxLength={200}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                        rows={2}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        When enabled, this notice is spoken before your greeting.
                      </p>
                    </>
                  )}
                  {!settings.recording_notice_enabled && (
                    <p className="text-xs text-gray-500">
                      Recording notice is OFF. Only your welcome greeting will be spoken.
                    </p>
                  )}
                </div>

                {/* Welcome Greeting */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Welcome Greeting
                  </label>
                  <textarea
                    value={settings.welcome_greeting}
                    onChange={(e) => updateSetting('welcome_greeting', e.target.value)}
                    placeholder="Hi! I'm your AI assistant for [Restaurant]. How can I help you today?"
                    maxLength={500}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none ${
                      settings.welcome_greeting && !settings.welcome_greeting.toLowerCase().includes('ai')
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    rows={3}
                  />
                  {settings.welcome_greeting && !settings.welcome_greeting.toLowerCase().includes('ai') && (
                    <p className="text-xs text-red-500 mt-1">
                      Your greeting must include "AI" (e.g., "AI assistant", "AI host") to comply with FCC disclosure requirements.
                    </p>
                  )}
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      Spoken by your AI voice as the first thing callers hear. Must include "AI" for FCC compliance.
                    </p>
                    <span className={`text-xs ${(settings.welcome_greeting?.length || 0) > 450 ? 'text-orange-500' : 'text-gray-400'}`}>
                      {settings.welcome_greeting?.length || 0}/500
                    </span>
                  </div>
                </div>

                {/* Holiday Greeting */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Holiday Greeting
                      </label>
                      <p className={`text-sm mt-1 ${getHolidayStatus(settings.holiday_greeting_enabled, settings.holiday_greeting_start_date, settings.holiday_greeting_end_date, settings.holiday_message).color}`}>
                        {getHolidayStatus(settings.holiday_greeting_enabled, settings.holiday_greeting_start_date, settings.holiday_greeting_end_date, settings.holiday_message).label}
                      </p>
                    </div>
                    <button
                      onClick={() => updateSetting('holiday_greeting_enabled', !settings.holiday_greeting_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.holiday_greeting_enabled ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.holiday_greeting_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {settings.holiday_greeting_enabled && (
                    <>
                      <textarea
                        value={settings.holiday_message}
                        onChange={(e) => updateSetting('holiday_message', e.target.value)}
                        placeholder="Happy Holidays! We hope you're enjoying the season..."
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                        rows={3}
                      />
                      <div className="flex justify-end mb-4">
                        <span className={`text-xs ${(settings.holiday_message?.length || 0) > 450 ? 'text-orange-500' : 'text-gray-400'}`}>
                          {settings.holiday_message?.length || 0}/500
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={settings.holiday_greeting_start_date}
                            onChange={(e) => updateSetting('holiday_greeting_start_date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">End Date</label>
                          <input
                            type="date"
                            value={settings.holiday_greeting_end_date}
                            onChange={(e) => updateSetting('holiday_greeting_end_date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Speech Intelligence Section */}
            {activeSection === 'speech' && (
              <div className="space-y-6">
                {/* Cuisine Type Selection */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Languages className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Speech Intelligence</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Configure how the AI understands and pronounces your menu items. Select your cuisine type, then use the AI generator or add manual overrides below.
                  </p>

                  <label className="block text-sm font-medium text-gray-700 mb-2">Cuisine Type</label>
                  <Select
                    value={settings.cuisine_type || '__none__'}
                    onValueChange={(val) => handleCuisineTypeChange(val === '__none__' ? '' : val)}
                  >
                    <SelectTrigger className="w-full max-w-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white">
                      <SelectValue placeholder="Select a cuisine type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl shadow-lg border border-gray-200 p-1">
                      <SelectItem value="__none__" className="rounded-lg py-2.5 pl-9 pr-3 text-gray-400 cursor-pointer hover:bg-gray-50">
                        None
                      </SelectItem>
                      {cuisineTemplates.map((t) => (
                        <SelectItem key={t.cuisine_type} value={t.cuisine_type} className="rounded-lg py-2.5 pl-9 pr-3 cursor-pointer hover:bg-purple-50">
                          {t.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">
                    Selects a global speech template. Your restaurant overrides are layered on top.
                  </p>
                </div>

                {/* AI Speech Generator */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200 shadow-sm p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h4 className="text-sm font-semibold text-gray-900">AI Speech Generator</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Search for a menu item to auto-generate hearing corrections, aliases, and pronunciation guides.
                  </p>

                  {/* Search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={speechMenuSearch}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSpeechMenuSearch(val);
                        if (val.length < 2) {
                          setSpeechSuggestions(null);
                          setSpeechSelectedItem('');
                        }
                      }}
                      placeholder="Search menu items..."
                      className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                    />
                    {speechMenuSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setSpeechMenuSearch('');
                          setSpeechSuggestions(null);
                          setSpeechSelectedItem('');
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Search Results */}
                  {speechMenuSearch.length >= 2 && (
                    <div className="bg-white rounded-lg border border-gray-200 max-h-48 overflow-y-auto mb-4">
                      {speechMenuItems
                        .filter(item =>
                          item.item_name.toLowerCase().includes(speechMenuSearch.toLowerCase())
                        )
                        .slice(0, 10)
                        .map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between px-3 py-2 hover:bg-purple-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-800 truncate block">{item.item_name}</span>
                              {item.section && (
                                <span className="text-xs text-gray-400">{item.section}</span>
                              )}
                            </div>
                            <button
                              onClick={() => generateSpeechForItem(item)}
                              disabled={speechGenerating}
                              className="ml-2 flex items-center gap-1 px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 disabled:opacity-50 whitespace-nowrap"
                            >
                              <Wand2 className="w-3 h-3" />
                              Generate
                            </button>
                          </div>
                        ))
                      }
                      {speechMenuItems.filter(item =>
                        item.item_name.toLowerCase().includes(speechMenuSearch.toLowerCase())
                      ).length === 0 && (
                        <p className="text-xs text-gray-400 px-3 py-3 text-center">No items found</p>
                      )}
                    </div>
                  )}

                  {/* Loading */}
                  {speechGenerating && (
                    <div className="flex items-center gap-2 py-4 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                      <span className="text-sm text-gray-600">Generating speech intelligence for "{speechSelectedItem}"...</span>
                    </div>
                  )}

                  {/* Generated Suggestions */}
                  {speechSuggestions && !speechGenerating && (
                    <div className="space-y-4 mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Suggestions for "{speechSuggestions.item_name}"
                        </span>
                      </div>

                      {/* Hearing Corrections */}
                      {Object.keys(speechSuggestions.hearing_corrections).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-2">Hearing Corrections</p>
                          <div className="space-y-1.5">
                            {Object.entries(speechSuggestions.hearing_corrections).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-gray-200">
                                <span className="text-xs text-gray-600 flex-1">
                                  <span className="bg-blue-50 px-1.5 py-0.5 rounded">"{key}"</span>
                                  <span className="text-gray-400 mx-1">&rarr;</span>
                                  <span className="font-medium">{value}</span>
                                </span>
                                <button
                                  onClick={() => addSuggestionToOverrides('hearing_corrections', key, value)}
                                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100"
                                >
                                  <Plus className="w-3 h-3" /> Add
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Aliases */}
                      {Object.keys(speechSuggestions.alias_map).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-2">Item Aliases</p>
                          <div className="space-y-1.5">
                            {Object.entries(speechSuggestions.alias_map).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-gray-200">
                                <span className="text-xs text-gray-600 flex-1">
                                  <span className="bg-green-50 px-1.5 py-0.5 rounded">"{key}"</span>
                                  <span className="text-gray-400 mx-1">&rarr;</span>
                                  <span className="font-medium">{value}</span>
                                </span>
                                <button
                                  onClick={() => addSuggestionToOverrides('alias_map', key, value)}
                                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100"
                                >
                                  <Plus className="w-3 h-3" /> Add
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pronunciation */}
                      {Object.keys(speechSuggestions.speaking_guide).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-2">Pronunciation Guide</p>
                          <div className="space-y-1.5">
                            {Object.entries(speechSuggestions.speaking_guide).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-gray-200">
                                <span className="text-xs text-gray-600 flex-1">
                                  <span className="bg-purple-50 px-1.5 py-0.5 rounded">{key}</span>
                                  <span className="text-gray-400 mx-1">&rarr;</span>
                                  <span className="font-medium">"{value}"</span>
                                </span>
                                <button
                                  onClick={() => addSuggestionToOverrides('speaking_guide', key, value)}
                                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100"
                                >
                                  <Plus className="w-3 h-3" /> Add
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add All button */}
                      {(Object.keys(speechSuggestions.hearing_corrections).length +
                        Object.keys(speechSuggestions.alias_map).length +
                        Object.keys(speechSuggestions.speaking_guide).length) > 0 && (
                        <button
                          onClick={() => {
                            let count = 0;
                            Object.entries(speechSuggestions.hearing_corrections).forEach(([k, v]) => {
                              addKeyValuePair('hearing_corrections', k, v);
                              count++;
                            });
                            Object.entries(speechSuggestions.alias_map).forEach(([k, v]) => {
                              addKeyValuePair('alias_map', k, v);
                              count++;
                            });
                            Object.entries(speechSuggestions.speaking_guide).forEach(([k, v]) => {
                              addKeyValuePair('speaking_guide', k, v);
                              count++;
                            });
                            setSpeechSuggestions(null);
                            toast.success(`Added ${count} items to overrides`);
                          }}
                          className="w-full py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add All Suggestions ({
                            Object.keys(speechSuggestions.hearing_corrections).length +
                            Object.keys(speechSuggestions.alias_map).length +
                            Object.keys(speechSuggestions.speaking_guide).length
                          })
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Auto-Dictionary (Batch AI Generation) — HIDDEN: auto-dict disabled Mar 26 2026 */}
                {false && <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Wand2 className="w-5 h-5 text-amber-600" />
                      <h4 className="text-sm font-semibold text-gray-900">Auto-Dictionary</h4>
                      {autoDictData && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${autoDictEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {autoDictEnabled ? 'Active' : 'Disabled'}
                        </span>
                      )}
                    </div>
                    {autoDictData && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-gray-500">{autoDictEnabled ? 'On' : 'Off'}</span>
                        <button
                          type="button"
                          onClick={() => toggleAutoDictionary(!autoDictEnabled)}
                          disabled={autoDictToggling}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoDictEnabled ? 'bg-amber-500' : 'bg-gray-300'} ${autoDictToggling ? 'opacity-50' : ''}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${autoDictEnabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                        </button>
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    AI-generates hearing corrections, aliases, and pronunciation guides for ALL menu items in one batch.
                    {autoDictData ? ' Toggle off to disable without losing generated data.' : ' Takes ~30-60 seconds.'}
                  </p>

                  {/* Stats (if data exists) */}
                  {autoDictData && (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-white/70 rounded-lg p-3 text-center border border-amber-100">
                        <div className="text-lg font-bold text-amber-700">
                          {Object.keys(autoDictData.hearing_corrections || {}).length}
                        </div>
                        <div className="text-xs text-gray-500">Corrections</div>
                      </div>
                      <div className="bg-white/70 rounded-lg p-3 text-center border border-amber-100">
                        <div className="text-lg font-bold text-amber-700">
                          {Object.keys(autoDictData.alias_map || {}).length}
                        </div>
                        <div className="text-xs text-gray-500">Aliases</div>
                      </div>
                      <div className="bg-white/70 rounded-lg p-3 text-center border border-amber-100">
                        <div className="text-lg font-bold text-amber-700">
                          {Object.keys(autoDictData.speaking_guide || {}).length}
                        </div>
                        <div className="text-xs text-gray-500">Pronunciations</div>
                      </div>
                    </div>
                  )}

                  {/* Generated timestamp */}
                  {autoDictGeneratedAt && (
                    <p className="text-xs text-gray-400 mb-3">
                      Last generated: {new Date(autoDictGeneratedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  )}

                  {/* Generate / Regenerate button */}
                  <button
                    type="button"
                    onClick={generateAutoDictionary}
                    disabled={autoDictGenerating}
                    className="w-full py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {autoDictGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating for all menu items...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        {autoDictData ? 'Regenerate Auto-Dictionary' : 'Generate Auto-Dictionary'}
                      </>
                    )}
                  </button>
                </div>}

                {/* Restaurant Overrides — grouped cards */}
                <SpeechOverridesPanel
                  speechOverrides={settings.speech_overrides}
                  onUpdateOverride={updateSpeechOverride}
                  menuItems={speechMenuItems}
                  onShowToast={(msg) => toast.info(msg)}
                />
              </div>
            )}

            {/* Operating Hours Section */}
            {activeSection === 'hours' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Operating Hours</h2>
                </div>

                {/* Timezone & Wait Times */}
                <div className="mb-6 p-5 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="grid grid-cols-3 gap-6">
                    {/* Timezone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Timezone
                      </label>
                      <Select
                        value={settings.timezone || 'America/New_York'}
                        onValueChange={(val) => updateSetting('timezone', val)}
                      >
                        <SelectTrigger className="w-full border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white rounded-xl shadow-lg border border-gray-200 p-1">
                          <SelectItem value="America/New_York" className="rounded-lg py-2.5 pl-9 pr-3 cursor-pointer hover:bg-purple-50">
                            <span className="text-gray-400 mr-2 font-mono text-xs">UTC-05:00</span> Eastern
                          </SelectItem>
                          <SelectItem value="America/Chicago" className="rounded-lg py-2.5 pl-9 pr-3 cursor-pointer hover:bg-purple-50">
                            <span className="text-gray-400 mr-2 font-mono text-xs">UTC-06:00</span> Central
                          </SelectItem>
                          <SelectItem value="America/Denver" className="rounded-lg py-2.5 pl-9 pr-3 cursor-pointer hover:bg-purple-50">
                            <span className="text-gray-400 mr-2 font-mono text-xs">UTC-07:00</span> Mountain
                          </SelectItem>
                          <SelectItem value="America/Los_Angeles" className="rounded-lg py-2.5 pl-9 pr-3 cursor-pointer hover:bg-purple-50">
                            <span className="text-gray-400 mr-2 font-mono text-xs">UTC-08:00</span> Pacific
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Estimated Wait Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Estimated Wait Time
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="5"
                          max="120"
                          value={settings.wait_time_minutes}
                          onChange={(e) => updateSetting('wait_time_minutes', parseInt(e.target.value) || 15)}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <span className="text-sm text-gray-500">minutes</span>
                      </div>
                    </div>
                    {/* Next Shift Prep Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Next Shift Prep Time
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="5"
                          max="120"
                          value={settings.next_shift_prep_minutes}
                          onChange={(e) => updateSetting('next_shift_prep_minutes', parseInt(e.target.value) || 30)}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <span className="text-sm text-gray-500">minutes</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Used when orders roll to next shift after last-order cutoff</p>
                    </div>
                  </div>
                </div>

                {/* Days - Compact layout */}
                <div className="space-y-2">
                  {DAYS_OF_WEEK.map((day) => {
                    const isWorking = settings.working_days?.includes(day.key);
                    const hours = settings.operating_hours?.[day.key];
                    const isSplit = isSplitShift(hours);

                    return (
                      <div key={day.key} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        {/* Toggle */}
                        <button
                          onClick={() => toggleWorkingDay(day.key)}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                            isWorking ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              isWorking ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>

                        {/* Day name */}
                        <span className={`w-24 text-sm font-medium ${isWorking ? 'text-gray-900' : 'text-gray-400'}`}>
                          {day.label}
                        </span>

                        {/* Hours */}
                        <div className="flex-1">
                          {isWorking ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              {isSplit ? (
                                // Split shift - show both shifts inline
                                <div className="flex items-center gap-3 flex-wrap">
                                  {(hours as Shift[]).map((shift, idx) => (
                                    <div key={idx} className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded">
                                      <span className="text-xs text-purple-600 font-medium">{shift.label}</span>
                                      <input
                                        type="time"
                                        value={shift.open}
                                        onChange={(e) => updateShiftHours(day.key, idx, 'open', e.target.value)}
                                        onBlur={() => validateFieldOnBlur(day.key, 'open', idx)}
                                        className={`w-[125px] px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-purple-500 ${hoursErrors[`${day.key}:open:${idx}`] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                        title={hoursErrors[`${day.key}:open:${idx}`] ? 'Open time must be before close time' : ''}
                                      />
                                      <span className="text-gray-400 text-xs">-</span>
                                      <input
                                        type="time"
                                        value={shift.close}
                                        onChange={(e) => updateShiftHours(day.key, idx, 'close', e.target.value)}
                                        onBlur={() => validateFieldOnBlur(day.key, 'close', idx)}
                                        className={`w-[125px] px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-purple-500 ${hoursErrors[`${day.key}:close:${idx}`] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                        title={hoursErrors[`${day.key}:close:${idx}`] ? 'Close time must be after open time' : ''}
                                      />
                                      <span className="text-xs text-orange-600 font-medium">Last order</span>
                                      <div className="relative">
                                        <input
                                          type="time"
                                          value={shift.last_order || ''}
                                          onChange={(e) => updateShiftHours(day.key, idx, 'last_order', e.target.value)}
                                          onBlur={() => validateFieldOnBlur(day.key, 'last_order', idx)}
                                          className={`w-[125px] px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-orange-500 ${hoursErrors[`${day.key}:last_order:${idx}`] ? 'border-red-500 bg-red-50' : 'border-orange-300'}`}
                                          title={hoursErrors[`${day.key}:last_order:${idx}`] ? 'Last order must be between open and close time' : 'Leave empty to accept orders until close'}
                                        />
                                        {shift.last_order && (
                                          <button
                                            type="button"
                                            onClick={() => updateShiftHours(day.key, idx, 'last_order', '')}
                                            className="absolute -right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-gray-300 hover:bg-red-400 text-white text-[9px] leading-none"
                                            title="Clear last order (accept orders until close)"
                                          >×</button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                // Single shift
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="time"
                                    value={(hours as { open: string; close: string })?.open || '09:00'}
                                    onChange={(e) => updateHours(day.key, 'open', e.target.value)}
                                    onBlur={() => validateFieldOnBlur(day.key, 'open')}
                                    className={`w-[125px] px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-purple-500 ${hoursErrors[`${day.key}:open`] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                    title={hoursErrors[`${day.key}:open`] ? 'Open time must be before close time' : ''}
                                  />
                                  <span className="text-gray-400 text-xs">-</span>
                                  <input
                                    type="time"
                                    value={(hours as { open: string; close: string })?.close || '21:00'}
                                    onChange={(e) => updateHours(day.key, 'close', e.target.value)}
                                    onBlur={() => validateFieldOnBlur(day.key, 'close')}
                                    className={`w-[125px] px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-purple-500 ${hoursErrors[`${day.key}:close`] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                    title={hoursErrors[`${day.key}:close`] ? 'Close time must be after open time' : ''}
                                  />
                                  <span className="text-xs text-orange-600 font-medium">Last order</span>
                                  <div className="relative">
                                    <input
                                      type="time"
                                      value={(hours as any)?.last_order || ''}
                                      onChange={(e) => updateHours(day.key, 'last_order', e.target.value)}
                                      onBlur={() => validateFieldOnBlur(day.key, 'last_order')}
                                      className={`w-[125px] px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-orange-500 ${hoursErrors[`${day.key}:last_order`] ? 'border-red-500 bg-red-50' : 'border-orange-300'}`}
                                      title={hoursErrors[`${day.key}:last_order`] ? 'Last order must be between open and close time' : 'Leave empty to accept orders until close'}
                                    />
                                    {(hours as any)?.last_order && (
                                      <button
                                        type="button"
                                        onClick={() => updateHours(day.key, 'last_order', '')}
                                        className="absolute -right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-gray-300 hover:bg-red-400 text-white text-[9px] leading-none"
                                        title="Clear last order (accept orders until close)"
                                      >×</button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">Closed</span>
                          )}
                        </div>

                        {/* Action buttons - only show when working */}
                        {isWorking && (
                          <div className="flex items-center gap-1">
                            {/* Split/Merge toggle icon */}
                            <button
                              onClick={() => toggleSplitShift(day.key)}
                              title={isSplit ? 'Merge to single shift' : 'Split into Lunch/Dinner'}
                              className={`p-1.5 rounded transition-colors ${
                                isSplit
                                  ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                  : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                              </svg>
                            </button>
                            {/* Copy to all days */}
                            <button
                              onClick={() => copyHoursToAll(day.key)}
                              title="Copy to all days"
                              className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Branding Section */}
            {activeSection === 'branding' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Image className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Branding</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Upload your restaurant logo. It will appear in SMS link previews.
                </p>

                {/* Logo Preview */}
                <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 mb-4">
                  {logoUrl ? (
                    <div className="relative group">
                      <img
                        src={logoUrl}
                        alt="Restaurant Logo"
                        className="max-w-[200px] max-h-[120px] object-contain rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          onClick={handleLogoDelete}
                          disabled={deletingLogo}
                          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full"
                        >
                          {deletingLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-3 bg-gray-200 rounded-full flex items-center justify-center">
                        <ImagePlus className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm">No logo uploaded</p>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex items-center justify-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`flex items-center gap-2 px-4 py-2 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-medium cursor-pointer ${
                      uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <ImagePlus className="w-4 h-4" />
                        <span>{logoUrl ? 'Replace Logo' : 'Upload Logo'}</span>
                      </>
                    )}
                  </label>
                </div>
                <p className="text-xs text-gray-400 text-center mt-3">PNG, JPEG, or WebP - Max 2MB</p>
              </div>
            )}

            {/* Contact & Support Section */}
            {activeSection === 'contact' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Contact & Support</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Contact info for when callers need to speak with a human.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={settings.human_contact_name}
                      onChange={(e) => updateSetting('human_contact_name', e.target.value)}
                      placeholder="e.g., Manager on Duty"
                      maxLength={100}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                    <input
                      type="tel"
                      value={settings.human_contact_phone}
                      onChange={(e) => {
                        updateSetting('human_contact_phone', formatPhoneNumber(e.target.value));
                        setFieldErrors(prev => ({ ...prev, phone: '' }));
                      }}
                      onBlur={() => {
                        const digits = (settings.human_contact_phone || '').replace(/\D/g, '');
                        if (digits.length > 0 && digits.length < 10) {
                          setFieldErrors(prev => ({ ...prev, phone: 'Phone must be 10 digits' }));
                        }
                      }}
                      placeholder="(555) 123-4567"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${fieldErrors.phone ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {fieldErrors.phone && <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                    <input
                      type="email"
                      value={settings.human_contact_email}
                      onChange={(e) => {
                        updateSetting('human_contact_email', e.target.value);
                        setFieldErrors(prev => ({ ...prev, email: '' }));
                      }}
                      onBlur={() => {
                        const val = settings.human_contact_email?.trim();
                        if (val && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) {
                          setFieldErrors(prev => ({ ...prev, email: 'Please enter a valid email' }));
                        }
                      }}
                      placeholder="manager@restaurant.com"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${fieldErrors.email ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Payments Section */}
            {activeSection === 'payments' && (
              <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Payment Options</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Choose how customers pay for their orders placed through AI Host.
                </p>

                <div className="space-y-4">
                  {/* Pay at Pickup */}
                  <div
                    onClick={() => updateSetting('payment_provider', 'none')}
                    className={`cursor-pointer border rounded-lg p-4 transition-all ${
                      settings.payment_provider === 'none'
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        settings.payment_provider === 'none' ? 'border-purple-600' : 'border-gray-300'
                      }`}>
                        {settings.payment_provider === 'none' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Wallet className="w-5 h-5 text-orange-500" />
                          <span className="font-medium text-gray-900">Pay at Pickup Only</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Customers pay when they arrive. No online payment processing.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Online Payments */}
                  <div
                    onClick={() => {
                      if (settings.stripe_account_connected) {
                        updateSetting('payment_provider', 'stripe');
                      }
                    }}
                    className={`cursor-pointer border rounded-lg p-4 transition-all ${
                      settings.payment_provider === 'stripe'
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : settings.stripe_account_connected
                          ? 'border-gray-200 hover:border-gray-300'
                          : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        settings.payment_provider === 'stripe' ? 'border-purple-600' : 'border-gray-300'
                      }`}>
                        {settings.payment_provider === 'stripe' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-purple-500" />
                          <span className="font-medium text-gray-900">Accept Online Payments</span>
                          {!settings.stripe_account_connected && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                              Stripe not connected
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Customers receive an SMS payment link and pay online before pickup.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stripe Status */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" fill="#635BFF"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Stripe Connect</p>
                        <p className="text-sm text-gray-600">
                          {settings.stripe_account_connected
                            ? 'Connected and ready to accept payments'
                            : 'Connect Stripe to accept online payments'}
                        </p>
                      </div>
                    </div>
                    {settings.stripe_account_connected ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                        <CheckCircle className="w-4 h-4" />
                        Connected
                      </span>
                    ) : (
                      <button
                        onClick={async () => {
                          try {
                            setConnectingStripe(true);
                            // Create Stripe Express account - returns onboarding URL
                            const response = await apiClient.post('/stripe-connect/create-account', {});

                            if (response.data.already_exists) {
                              // Account exists, get fresh onboarding link
                              const linkResponse = await apiClient.get('/stripe-connect/onboarding-link');
                              if (linkResponse.data.url) {
                                window.open(linkResponse.data.url, '_blank');
                              } else {
                                toast.error('Stripe account exists but onboarding link could not be generated.');
                              }
                            } else if (response.data.onboarding_url) {
                              // New account created with onboarding URL - open in new tab
                              window.open(response.data.onboarding_url, '_blank');
                            } else {
                              toast.error('Failed to generate Stripe onboarding link. Please try again.');
                            }
                          } catch (error: any) {
                            console.error('Stripe Connect error:', error);
                            toast.error(error.response?.data?.detail || 'Failed to connect Stripe. Please try again.');
                          } finally {
                            setConnectingStripe(false);
                          }
                        }}
                        disabled={connectingStripe}
                        className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {connectingStripe ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Connecting...
                          </span>
                        ) : (
                          'Connect Stripe'
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4 text-sm text-gray-500 bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <span className="text-blue-600">Coming soon:</span> Additional payment providers including Fiserv and First Data.
                </div>
              </div>

              {/* Large Order Protection - only show when Stripe connected */}
              {settings.stripe_account_connected && settings.payment_provider === 'stripe' && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">Large Order Protection</h3>
                      <p className="text-sm text-gray-600">Require payment in advance for larger orders to reduce no-show risk</p>
                    </div>
                    <button
                      onClick={() => {
                        if (settings.require_payment_above !== null) {
                          updateSetting('require_payment_above', null);
                        } else {
                          updateSetting('require_payment_above', 70);
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.require_payment_above !== null ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.require_payment_above !== null ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {settings.require_payment_above !== null && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Required Threshold
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-lg">$</span>
                        <input
                          type="number"
                          min="0"
                          max="10000"
                          step="10"
                          value={settings.require_payment_above}
                          onChange={(e) => updateSetting('require_payment_above', parseFloat(e.target.value) || 0)}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Orders at or above this amount will be asked to pay in advance. Set to $0 to require payment for all orders.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Large Order Alerts - moved from notifications */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">Large Order Alerts</h3>
                    <p className="text-sm text-gray-600">Flag orders exceeding a value threshold for review</p>
                  </div>
                  <button
                    onClick={() => updateSetting('enable_large_order_alerts', !settings.enable_large_order_alerts)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.enable_large_order_alerts ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.enable_large_order_alerts ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {settings.enable_large_order_alerts && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alert Threshold ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100000"
                      step="50"
                      value={settings.large_order_value_threshold}
                      onChange={(e) => updateSetting('large_order_value_threshold', parseInt(e.target.value) || 500)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Orders above this amount trigger a "Needs Review" flag in the dashboard.
                    </p>
                  </div>
                )}
              </div>
              </div>
            )}

            {/* POS Integration Section */}
            {activeSection === 'pos' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Monitor className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">POS Integration</h2>
                </div>
                <p className="text-sm text-gray-500 mb-6">
                  Connect your POS system to automatically send confirmed orders to your kitchen display.
                </p>

                {/* Connection Status */}
                <div className={`rounded-lg border p-4 mb-6 ${
                  settings.pos_connection_status === 'connected'
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        settings.pos_connection_status === 'connected' ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <div>
                        <p className="font-medium text-gray-900">
                          {settings.pos_connection_status === 'connected'
                            ? `${(settings.pos_provider || '').charAt(0).toUpperCase() + (settings.pos_provider || '').slice(1)} Connected`
                            : 'No POS Connected'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {settings.pos_connection_status === 'connected' && settings.pos_last_synced
                            ? `Last synced: ${new Date(settings.pos_last_synced).toLocaleDateString()}`
                            : settings.pos_connection_status === 'connected'
                              ? 'Connected and ready'
                              : 'Connect a POS system to enable order sync'}
                        </p>
                      </div>
                    </div>
                    {settings.pos_connection_status !== 'connected' && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        Contact support to connect
                      </span>
                    )}
                  </div>
                </div>

                {/* POS Sync Toggle — only show when connected */}
                {settings.pos_connection_status === 'connected' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900">Order Sync</p>
                        <p className="text-sm text-gray-500">Automatically send confirmed orders to your POS</p>
                      </div>
                      <button
                        onClick={() => updateSetting('pos_sync_enabled', !settings.pos_sync_enabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.pos_sync_enabled ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.pos_sync_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900">Kitchen Printing</p>
                        <p className="text-sm text-gray-500">Print kitchen tickets when orders are synced</p>
                      </div>
                      <button
                        onClick={() => updateSetting('kitchen_print_enabled', !settings.kitchen_print_enabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.kitchen_print_enabled ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.kitchen_print_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notifications Section - Simplified (Large Order Alerts moved to Payments) */}
            {activeSection === 'notifications' && (
              <>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-purple-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Order Notifications</h2>
                  </div>
                  <button
                    onClick={() => updateSetting('order_notifications_enabled', !settings.order_notifications_enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.order_notifications_enabled ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.order_notifications_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Send SMS notifications when new orders come in.
                </p>

                {settings.order_notifications_enabled && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">Phone numbers to notify:</p>
                    {[0, 1, 2].map((index) => (
                      <div key={index}>
                        <input
                          type="tel"
                          value={settings.order_notify_phones?.[index] || ''}
                          onChange={(e) => {
                            updateNotifyPhone(index, e.target.value);
                            setFieldErrors(prev => ({ ...prev, [`notify_${index}`]: '' }));
                          }}
                          onBlur={() => {
                            const digits = (settings.order_notify_phones?.[index] || '').replace(/\D/g, '');
                            if (digits.length > 0 && digits.length < 10) {
                              setFieldErrors(prev => ({ ...prev, [`notify_${index}`]: 'Phone must be 10 digits' }));
                            }
                          }}
                          placeholder={`Phone ${index + 1}${index === 0 ? '' : ' (optional)'}`}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${fieldErrors[`notify_${index}`] ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {fieldErrors[`notify_${index}`] && <p className="text-xs text-red-500 mt-1">{fieldErrors[`notify_${index}`]}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Safety & Compliance */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-purple-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Allergen Transfer</h2>
                  </div>
                  <button
                    onClick={() => updateSetting('transfer_on_allergen_mention', !settings.transfer_on_allergen_mention)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.transfer_on_allergen_mention ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.transfer_on_allergen_mention ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Automatically transfer to staff when a customer mentions any food allergy or allergen concern.
                </p>
                <p className="text-xs text-gray-500">
                  Recommended: Keep this ON for liability protection. The AI will immediately connect
                  allergen-related calls to your staff rather than attempting to answer from menu data.
                </p>
              </div>
              </>
            )}

            {/* Special Services Section - Reservations, Catering, Events */}
            {activeSection === 'services' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Special Services</h2>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  When enabled, AI Host will collect customer details for these services and refer them to your human contact for follow-up.
                </p>

                <div className="space-y-4">
                  {/* Reservations */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-medium text-gray-900">Reservations</p>
                      <p className="text-sm text-gray-600">AI Host collects reservation requests and refers them to your team</p>
                    </div>
                    <button
                      onClick={() => updateSetting('enable_reservations', !settings.enable_reservations)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enable_reservations ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enable_reservations ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Catering */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-medium text-gray-900">Catering</p>
                      <p className="text-sm text-gray-600">AI Host collects catering inquiries and refers them to your team</p>
                    </div>
                    <button
                      onClick={() => updateSetting('enable_catering', !settings.enable_catering)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enable_catering ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enable_catering ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Events */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-medium text-gray-900">Events & Private Parties</p>
                      <p className="text-sm text-gray-600">AI Host collects event inquiries and refers them to your team</p>
                    </div>
                    <button
                      onClick={() => updateSetting('enable_events', !settings.enable_events)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.enable_events ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.enable_events ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Human Contact Reminder */}
                {(settings.enable_reservations || settings.enable_catering || settings.enable_events) && (
                  <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-700">
                      <strong>Reminder:</strong> Make sure you've configured your human contact details in the{' '}
                      <button
                        onClick={() => {
                          setActiveSection('contact');
                          window.location.hash = 'contact';
                        }}
                        className="underline font-medium hover:text-purple-800"
                      >
                        Contact & Support
                      </button>{' '}
                      section so customers know who to reach.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Team & Access Section */}
            {activeSection === 'team' && !isStaff && (
              <div className="space-y-6">
                <VHStaffTeamManagement />

                {/* Staff Portal URL */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Link className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">Staff Portal URL</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Create a short, memorable URL for your staff to access the order portal.
                  </p>

                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">dineai.ai/staff/</span>
                    <input
                      type="text"
                      value={vanityCode}
                      onChange={(e) => handleVanityCodeChange(e.target.value)}
                      placeholder={vanityCodeSuggestion || 'your-restaurant'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={handleSaveVanityCode}
                      disabled={saving || (vanityCodeStatus !== 'available' && vanityCode !== '')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                  </div>

                  {vanityCodeStatus !== 'idle' && (
                    <div className={`flex items-center gap-2 mt-2 text-sm ${
                      vanityCodeStatus === 'available' ? 'text-green-600' :
                      vanityCodeStatus === 'checking' ? 'text-gray-500' : 'text-red-600'
                    }`}>
                      {vanityCodeStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin" />}
                      {vanityCodeStatus === 'available' && <CheckCircle className="w-4 h-4" />}
                      {(vanityCodeStatus === 'taken' || vanityCodeStatus === 'invalid') && <XCircle className="w-4 h-4" />}
                      <span>{vanityCodeMessage}</span>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    3-50 characters, lowercase letters, numbers, and hyphens only
                  </p>
                </div>
              </div>
            )}

            {/* Billing & Usage Section */}
            {activeSection === 'billing' && !isStaff && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Receipt className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Convenience Fee Summary</h2>
                </div>

                {loadingBilling ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                ) : !billingData?.has_subscription ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <p className="text-gray-600">No active subscription found.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Billing Period */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Current Billing Period</p>
                      <p className="font-medium text-gray-900">
                        {billingData.billing_period_start && new Date(billingData.billing_period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' - '}
                        {billingData.billing_period_end && new Date(billingData.billing_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-purple-600 mt-1">{billingData.plan_name}</p>
                    </div>

                    {/* Fee Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Wallet className="w-5 h-5 text-orange-500" />
                          <h4 className="font-medium text-gray-900">Pay at Pickup</h4>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Orders</span>
                            <span className="font-medium">{billingData.pickup_orders?.count || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Order Value</span>
                            <span className="font-medium">${(billingData.pickup_orders?.gmv || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-600">Convenience Fees</span>
                            <span className="font-semibold text-orange-600">${(billingData.pickup_orders?.fees || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <CreditCard className="w-5 h-5 text-purple-500" />
                          <h4 className="font-medium text-gray-900">Online Payments</h4>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Orders</span>
                            <span className="font-medium">{billingData.stripe_orders?.count || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Order Value</span>
                            <span className="font-medium">${(billingData.stripe_orders?.gmv || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-600">Convenience Fees</span>
                            <span className="font-semibold text-purple-600">${(billingData.stripe_orders?.fees || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-lg p-4 border border-purple-100">
                      <h4 className="font-medium text-gray-900 mb-3">Period Totals</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{billingData.total_orders || 0}</p>
                          <p className="text-sm text-gray-600">Total Orders</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">${(billingData.total_gmv || 0).toFixed(2)}</p>
                          <p className="text-sm text-gray-600">Order Value</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-purple-600">${(billingData.total_fees || 0).toFixed(2)}</p>
                          <p className="text-sm text-gray-600">Total Fees</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm border border-gray-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Unsaved Changes</h3>
            </div>
            <p className="text-gray-600 mb-6">You have unsaved changes. Would you like to save before leaving?</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowUnsavedModal(false);
                  setIsDirty(false);
                  navigate(isStaff ? '/vh-staff-dashboard' : '/restaurant-dashboard');
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  setShowUnsavedModal(false);
                  await handleSave();
                  setIsDirty(false);
                  navigate(isStaff ? '/vh-staff-dashboard' : '/restaurant-dashboard');
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#FDB913] to-purple-500 text-white rounded-lg hover:from-[#e5a511] hover:to-purple-600 font-medium transition-all"
              >
                Save & Leave
              </button>
            </div>
            <button
              onClick={() => setShowUnsavedModal(false)}
              className="w-full mt-3 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AIHostSettingsFullPage;
